import { Form, StyledRow } from '@/components';
import { ItemMenuCategoryDto, ItemMenuItemType } from '@/core';
import {
  CategoryState,
  ItemMenuItemDetailsState,
  ItemState,
  useGetCategoriesQuery,
  useGetItemsListQuery,
} from '@/redux';
import { Alert, Button, Col, Space, Typography } from 'antd';
import { useFormikContext } from 'formik';
import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import * as Yup from 'yup';
import { FormItemMenuItems } from './FormItemMenuItems';
import {
  FormItemMenuItemSuboptions,
  MenuItemCategoryFormValue,
  MenuItemFormValue,
  MenuItemSuboptionsFormValue,
} from './FormItemMenuItemSuboptions';
import { array } from '@/utils';
import { getCategoriesSchema } from './getCategoriesSchema';

function getSchema(categories: CategoryState[]) {
  return Yup.object().shape({
    name: Yup.string().field().nullable().required(),
    items: Yup.array().of(
      Yup.object().shape({
        itemId: Yup.string().nullable().required(),
        type: Yup.string().nullable().required(),
        standardCredit: Yup.bool().nullable(),
        suboptions: Yup.object().shape({
          all: Yup.bool().required(),
          pristine: Yup.bool(),
          noSuboptions: Yup.bool().nullable(),
          values: Yup.array()
            .of(
              Yup.object().shape({
                id: Yup.string().nullable().required(),
                type: Yup.string().nullable().required(),
              }),
            )
            .when(['all', 'pristine', 'noSuboptions'], {
              is: (
                all: boolean,
                pristine: boolean,
                noSuboptions: boolean | null,
              ) => !all && !pristine && noSuboptions === false,
              then: (x) => x.required().min(1),
            }),
        }),
      }),
    ),
    categories: getCategoriesSchema(categories),
  });
}

export interface ItemMenuDetailsFormValue {
  name: string;
  items: MenuItemFormValue[];
  categories: Record<string, MenuItemCategoryFormValue>;
}

export interface ItemMenuDetailsSubmitValue {
  name: string;
  items: Array<{
    itemId: string;
    type: ItemMenuItemType;
    suboptions: MenuItemSuboptionsFormValue;
    standardCredit: boolean;
  }>;
  categories: ItemMenuCategoryDto[];
}

interface Props {
  value: ItemMenuDetailsFormValue;
  onSubmit: (value: ItemMenuDetailsSubmitValue) => any;
  onCancel: () => any;
  communityId: string;
  initialItems?: ItemMenuItemDetailsState[];
  texts: {
    title: React.ReactNode;
    saveBtn: React.ReactNode;
  };
  initialItemMenuCategories?: ItemMenuCategoryDto[];
}

export function Suboptions({ selectedId }: { selectedId: string | undefined }) {
  const {
    values: { items },
  } = useFormikContext<ItemMenuDetailsFormValue>();
  const itemIds = items.map((x) => x.itemId);
  const { t } = useTranslation(undefined, { keyPrefix: 'itemMenus.details' });

  if (!selectedId || !itemIds.includes(selectedId)) {
    return <>{t('itemNotSelected')}</>;
  }

  const itemIndex = items.findIndex((x) => x.itemId === selectedId);
  const item = items[itemIndex];
  const standardCreditDisabled = items.some(
    (x) =>
      (x.itemId !== selectedId &&
        x.categoryId === item.categoryId &&
        !!x.standardCredit) ||
      item.type !== ItemMenuItemType.Standard,
  );

  return (
    <FormItemMenuItemSuboptions
      itemName={`items[${itemIndex}]`}
      itemId={selectedId}
      key={selectedId}
      standardCreditDisabled={standardCreditDisabled}
    />
  );
}

export function ItemMenuDetailsForm(props: Props) {
  const {
    onSubmit,
    onCancel,
    value,
    texts,
    communityId,
    initialItems,
    initialItemMenuCategories,
  } = props;
  const { t } = useTranslation();

  const submit = useCallback(
    ({ name, items, categories }: ItemMenuDetailsFormValue) => {
      return onSubmit({
        name,
        items: items.map(({ itemId, type, suboptions, standardCredit }) => ({
          itemId,
          type,
          suboptions,
          standardCredit,
        })),
        categories: Object.values(categories),
      });
    },
    [onSubmit],
  );

  const { data: items = array.empty<ItemState>(), isLoading } =
    useGetItemsListQuery({
      communityId,
    });

  const { data: categories = array.empty<CategoryState>() } =
    useGetCategoriesQuery({ communityId, listed: true });

  const schema = getSchema(categories);
  if (isLoading) return <></>;

  return (
    <div>
      <Typography.Title className="mb-3" level={5}>
        {texts.title}
      </Typography.Title>
      <Form.Formik<ItemMenuDetailsFormValue>
        uid="itemMenu-details"
        i18n="itemMenus.details"
        initialValues={{ ...value }}
        validationSchema={schema}
        onSubmit={submit}
      >
        <Alert
          type="info"
          className="mb-3"
          description={t('itemMenus.details.editTip')}
        />
        <StyledRow gutter={[40, 0]}>
          <Col>
            <Space style={{ display: 'flex', justifyContent: 'space-between' }}>
              <Form.Input required name="name" />
              <StyledRow justify="end" className="mt-3">
                <Space size="middle">
                  <Button onClick={onCancel}>
                    {t('itemMenus.details.cancelButton')}
                  </Button>
                  <Form.Submit type="primary">{texts.saveBtn}</Form.Submit>
                </Space>
              </StyledRow>
            </Space>
            <FormItemMenuItems
              name="items"
              categoriesName="categories"
              communityId={communityId}
              className="mb-4"
              initialItems={initialItems}
              initialItemMenuCategories={initialItemMenuCategories}
              items={items}
            />
          </Col>
        </StyledRow>
      </Form.Formik>
    </div>
  );
}
